import type { AgencyRecord, AgencyUpsertPayload as AgencyUpsert } from '../../../features/agencies/agencies.types';
import type {
  AgentApprovalRequestRecord,
  AgentRecord,
  TaskRecord
} from '../../../features/agents/agents.types';
import type { PosRecord, PosUpsertPayload } from '../../../features/pos/pos.types';
import type {
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload
} from '../../../features/supervisors/supervisors.types';

export type { AgencyRecord, AgencyUpsertPayload as AgencyUpsert } from '../../../features/agencies/agencies.types';
export type {
  AgentApprovalRequestRecord,
  AgentRecord,
  TaskRecord
} from '../../../features/agents/agents.types';
export type { PosRecord, PosUpsertPayload } from '../../../features/pos/pos.types';
export type {
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload
} from '../../../features/supervisors/supervisors.types';

export interface AuthCredentials {
  email: string;
  password: string;
}

export type UserLanguage = 'en' | 'fr';

export interface AuthUser {
  email: string;
  role: 'admin' | 'supervisor' | 'agent';
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  avatar?: string;
  language?: UserLanguage;
  roles?: string[];
  customRbacRoles?: string[];
  status?: 'active' | 'inactive' | 'pending';
  userType?: string;
  agency?: string;
  supervisor?: string;
}

export abstract class ApiRepository {
  abstract login(payload: AuthCredentials): Promise<AuthUser | null>;
  abstract requestSignup(payload: AuthCredentials & { fullName: string }): Promise<void>;
  abstract requestPasswordOtp(email: string): Promise<boolean>;
  abstract verifyPasswordOtp(email: string, otp: string): Promise<boolean>;
  abstract resetPassword(email: string, nextPassword: string): Promise<boolean>;

  abstract getAgencies(): Promise<AgencyRecord[]>;
  abstract getAgencyById(id: string): Promise<AgencyRecord | null>;
  abstract createAgency(payload: AgencyUpsert): Promise<AgencyRecord>;
  abstract updateAgency(id: string, payload: AgencyUpsert): Promise<AgencyRecord | null>;

  abstract getAgents(): Promise<AgentRecord[]>;
  abstract getAgentById(id: string): Promise<AgentRecord | null>;
  abstract getPendingAgentRequests(): Promise<AgentApprovalRequestRecord[]>;
  abstract approveAgentRequest(id: string): Promise<AgentRecord | null>;

  abstract getPos(): Promise<PosRecord[]>;
  abstract getPosById(id: string): Promise<PosRecord | null>;
  abstract createPos(payload: PosUpsertPayload): Promise<PosRecord>;
  abstract updatePos(id: string, payload: PosUpsertPayload): Promise<PosRecord | null>;
  abstract deletePos(id: string): Promise<boolean>;

  abstract getSupervisors(): Promise<SupervisorRecord[]>;
  abstract getPendingSupervisorRequests(): Promise<SupervisorApprovalRequestRecord[]>;
  abstract createSupervisor(payload: SupervisorUpsertPayload): Promise<SupervisorRecord>;
  abstract updateSupervisor(id: string, payload: SupervisorUpsertPayload): Promise<SupervisorRecord | null>;
  abstract approveSupervisorRequest(id: string): Promise<SupervisorRecord | null>;
  abstract deleteSupervisor(id: string): Promise<boolean>;

  abstract getTasks(): Promise<TaskRecord[]>;
}
